/**
 * apps/web/src/components/ErrorBoundary.tsx
 *
 * A React class error boundary — the ONLY mechanism that catches render-time
 * throws (this app uses <BrowserRouter> + <Routes>, so react-router's data-router
 * `errorElement`/`useRouteError` are inert here).
 *
 * Usage:
 *   - Top-level (main.tsx): wrap <App/> so a crash anywhere shows a recoverable
 *     screen instead of a white page; also catches stale-chunk load failures.
 *   - Per-route (Layout.tsx): wrap <Outlet/> and pass `resetKeys={[pathname]}`
 *     so navigating away auto-clears the error without remounting the shell, and
 *     `onReset={reset}` from React Query's <QueryErrorResetBoundary> so retrying
 *     also refetches the queries that threw.
 */

import React from 'react';
import { reportError } from '../lib/reportError';
import { RouteErrorFallback } from './RouteErrorFallback';

/** A dynamic-import / stale-chunk failure (vs. an ordinary app error). */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(error.message) ||
    /Failed to fetch dynamically imported module/i.test(error.message) ||
    /error loading dynamically imported module/i.test(error.message) ||
    /Importing a module script failed/i.test(error.message)
  );
}

interface FallbackArgs {
  readonly error: Error;
  readonly reset: () => void;
  readonly isChunkLoadError: boolean;
}

interface ErrorBoundaryProps {
  readonly children: React.ReactNode;
  /** Stable label forwarded to the telemetry sink. */
  readonly boundaryName?: string;
  /** Called after the boundary clears its error (e.g. React Query's reset). */
  readonly onReset?: () => void;
  /** When any value here changes while an error is shown, the boundary resets. */
  readonly resetKeys?: readonly unknown[];
  /** Override the default <RouteErrorFallback/>. */
  readonly fallback?: (args: FallbackArgs) => React.ReactNode;
  /** Default fallback layout when `fallback` is not provided. */
  readonly variant?: 'page' | 'inline';
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

function resetKeysChanged(a: readonly unknown[] = [], b: readonly unknown[] = []): boolean {
  if (a.length !== b.length) return true;
  for (let i = 0; i < a.length; i += 1) {
    if (!Object.is(a[i], b[i])) return true;
  }
  return false;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    reportError(error, {
      boundary: this.props.boundaryName ?? 'unknown',
      componentStack: info.componentStack ?? undefined,
    });
  }

  componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (this.state.error && resetKeysChanged(prev.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  private reset = (): void => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const args: FallbackArgs = {
      error,
      reset: this.reset,
      isChunkLoadError: isChunkLoadError(error),
    };

    if (this.props.fallback) return this.props.fallback(args);
    return (
      <RouteErrorFallback
        error={args.error}
        reset={args.reset}
        isChunkLoadError={args.isChunkLoadError}
        variant={this.props.variant ?? 'inline'}
      />
    );
  }
}
